import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth } from '../../firebase';
import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Skeleton,
  FormHelperText,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoApp from '../../img/logoGntunes.svg';
import Footer from '../footer';
import backgroundImage from '../../img/bg_image_texture3.png';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useHistory } from 'react-router-dom';
import { setLoader } from '../../redux';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { RegisterUser } from '../../_services/register';
import { getUserType } from '../../_services/userType/getUserType.service';
import { captchaGenerateService } from '../../_services/auth/auth.service';
import {
  validateAuthPassword,
  validateCaptchaValue,
  validateNomenclatureEmail,
} from '../../_helpers/reusablefunctions/validation-standard';
import { errorLogin } from '../../_helpers/reusablefunctions/errorLogin';

const EmailPasswordFirebaseSignup = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmpassword, setConfirmpassword] = useState('');
  const [showpassword, setShowpassword] = useState(false);
  const [showconfirmpassword, setShowConfirmpassword] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    confirmpassword: '',
    userTypeId: '',
    captcha: '',
  });
  const [userType, setUserType] = useState('');
  const [userTypeId, setUserTypeId] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getUserType()
      .then((r) => {
        if (r.data) {
          setUserType(r.data.data);
        }
      })
      .catch(() => {
        dispatch(setLoader(false));
      });
  }, [dispatch]);

  const fetchCaptcha = async ({ resetInput = false } = {}) => {
    const captchaResponse = await captchaGenerateService();
    const captchaData = captchaResponse?.data || {};
    setCaptchaKey(captchaData.captcha_key || '');
    setCaptchaText(captchaData.captcha_value || '');
    if (resetInput) {
      setCaptchaValue('');
      setErrors((prev) => ({ ...prev, captcha: '' }));
    }
    return captchaData;
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchCaptcha({ resetInput: true });
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  const handleEmailSignup = async () => {
    if (isSubmitting) return;

    let valid = true;
    const nextErrors = { email: '', password: '', confirmpassword: '', userTypeId: '', captcha: '' };

    if (!validateNomenclatureEmail(email).isValid) {
      nextErrors.email = 'Invalid email format.';
      valid = false;
    }

    if (!validateAuthPassword(password).isValid) {
      nextErrors.password =
        'Password must be at least 8 characters, include uppercase, lowercase, number and special character.';
      valid = false;
    }

    if (password !== confirmpassword) {
      nextErrors.confirmpassword = 'Passwords do not match.';
      valid = false;
    }

    if (!userTypeId) {
      nextErrors.userTypeId = 'Please select your primary role.';
      valid = false;
    }

    setErrors(nextErrors);
    if (!valid) return;

    const captchaValidation = validateCaptchaValue(captchaValue);
    if (!captchaValidation.isValid) {
      setErrors((prev) => ({ ...prev, captcha: captchaValidation.errors?.[0] || 'Please enter the captcha value.' }));
      return;
    }

    if (!captchaText || captchaValidation.value !== String(captchaText).trim()) {
      setErrors((prev) => ({ ...prev, captcha: 'Captcha value does not match.' }));
      return;
    }

    let activeCaptchaKey = captchaKey;
    if (!activeCaptchaKey) {
      const captchaData = await fetchCaptcha({ resetInput: true });
      activeCaptchaKey = captchaData?.captcha_key || '';
    }

    if (!activeCaptchaKey) {
      setErrors((prev) => ({ ...prev, captcha: 'Unable to load captcha. Please refresh and try again.' }));
      return;
    }

    setIsSubmitting(true);
    dispatch(setLoader(true));

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await sendEmailVerification(user);

      const response = await RegisterUser({
        creator_auth: user.uid,
        email,
        userType: userTypeId,
        captcha_key: activeCaptchaKey,
        captcha_value: captchaValue,
      });

      if (response?.status === 200 || response?.status === 201) {
        history.push('/thank-you');
        return;
      }

      const responseMessage = response?.data?.message || response?.data?.error?.[0] || 'Registration failed';
      if (String(responseMessage).toLowerCase().includes('captcha')) {
        setErrors((prev) => ({ ...prev, captcha: responseMessage }));
      } else {
        setErrors((prev) => ({ ...prev, email: responseMessage }));
      }
    } catch (signupError) {
      const errorMessage = errorLogin(signupError) || 'Registration failed';
      if (String(signupError?.message || '').toLowerCase().includes('captcha')) {
        setErrors((prev) => ({ ...prev, captcha: errorMessage }));
      } else {
        setErrors((prev) => ({ ...prev, email: errorMessage }));
      }
    } finally {
      setIsSubmitting(false);
      dispatch(setLoader(false));
    }
  };

  return (
    <Box
      className="container-fluid"
      style={{
        height: '100vh',
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <Box className="row">
        <nav className="navbar navbar-light border pb-0">
          <Box className="container-fluid">
            <a className="navbar-brand p-3" href="https://gntunes.com/">
              <img src={LogoApp} alt="logo" />
            </a>
          </Box>
        </nav>
        <Box style={{ maxWidth: '600px', width: '100%', margin: 'auto' }}>
          <Box className="mt-4 mb-4 p-4 border rounded shadow bg-white">
            <form className="row g-3">
              <h4 className="text-center">Email Sign Up</h4>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                error={!!errors.email}
                helperText={errors.email}
              />

              <TextField
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                type={showpassword ? 'text' : 'password'}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowpassword(!showpassword)}>
                      {showpassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  ),
                }}
              />

              <TextField
                label="Confirm Password"
                value={confirmpassword}
                onChange={(e) => setConfirmpassword(e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                type={showconfirmpassword ? 'text' : 'password'}
                error={!!errors.confirmpassword}
                helperText={
                  errors.confirmpassword ||
                  'Password must match the above field. Password must contain at least 8 characters, including at least 1 upper case letter, 1 lower case letter,1 number and 1 special character.'
                }
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowConfirmpassword(!showconfirmpassword)}>
                      {showconfirmpassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  ),
                }}
              />

              <Box className="mt-0">
                <Box className="mb-2">
                  <label>Your Primary Role *</label>
                </Box>
                {userType ? (
                  userType.map((data) => (
                    <div
                      key={data.id}
                      className="btn-group col-md-4 col-sm-12 col-12 col-lg-4 text-white p-0 pb-2"
                      role="group"
                      aria-label="Basic radio toggle button group"
                    >
                      <input
                        type="radio"
                        className="btn-check"
                        name="userType"
                        id={String(data.id)}
                        value={data.id}
                        onChange={(e) => setUserTypeId(e.target.value)}
                      />
                      <label className="btn btn-outline-danger overflow-hidden text-truncate" htmlFor={String(data.id)}>
                        <b>{data.userType}</b>
                      </label>
                    </div>
                  ))
                ) : (
                  <Skeleton height={60} animation="wave" />
                )}
                <FormHelperText sx={{ color: '#d32f2f' }}>{errors?.userTypeId}</FormHelperText>
              </Box>

              <>
                <Box className="d-flex align-items-center justify-content-between mt-1">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Enter captcha
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => fetchCaptcha({ resetInput: true })}
                    aria-label="Refresh captcha"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 1,
                    backgroundColor: '#fafafa',
                    fontWeight: 700,
                    letterSpacing: 1,
                    width: 'fit-content',
                  }}
                >
                  {captchaText || '-----'}
                </Box>
                <TextField
                  label="Captcha"
                  value={captchaValue}
                  onChange={(event) => {
                    setCaptchaValue(event.target.value);
                    if (errors.captcha) {
                      setErrors((prev) => ({ ...prev, captcha: '' }));
                    }
                  }}
                  fullWidth
                  margin="normal"
                  size="small"
                  error={Boolean(errors.captcha)}
                  helperText={errors.captcha}
                />
              </>

              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant="body2" sx={{ width: '55%' }}>
                  <span> Already have an account? </span>
                  <Link
                    to={{ pathname: '/login', state: { openEmailPhone: true } }}
                    className="gn-actionbtn-color fw-bold text-decoration-none"
                  >
                    Sign In
                  </Link>{' '}
                  or
                  <a className="gn-actionbtn-color fw-bold text-decoration-none" href="/connect-audio-platform">
                    {' '}
                    Connect with audio platform
                  </a>
                </Typography>
                <Button
                  variant="contained"
                  className="gn-actionbtn px-3 ms-3"
                  onClick={handleEmailSignup}
                  sx={{ mt: 2 }}
                  disabled={isSubmitting}
                >
                  Create Account
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default EmailPasswordFirebaseSignup;
